import { createContext, useContext, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { RESOURCE_ICONS } from '../../icons/resource-icons';
import type { ResourceKey } from '../../lib/game';
import { RESOURCES } from '../../lib/game';

/** Resolves a resource to its rendered-art sprite URL, or undefined to keep the vector glyph. */
export type ResourceIconResolver = (resource: ResourceKey) => string | undefined;

const ResourceIconContext = createContext<ResourceIconResolver | null>(null);

/**
 * Supplies bitmap art for every `ResourceIcon` beneath it, without
 * `packages/ui` depending on `@spacegame/assets` directly — the host app
 * (`apps/web`) wires `resolve` to its own sprite lookup. Without a provider,
 * `ResourceIcon` falls back to the vector glyph everywhere, so this stays
 * optional for other consumers of the design system.
 */
export function ResourceIconProvider({
  resolve,
  children,
}: {
  resolve: ResourceIconResolver;
  children: ReactNode;
}) {
  return <ResourceIconContext.Provider value={resolve}>{children}</ResourceIconContext.Provider>;
}

export interface ResourceIconProps {
  resource: ResourceKey;
  size?: number;
  /** Applies the resource's accent colour. Off when the parent already sets colour. Ignored once art is available — the rendered art already carries its own colour. */
  colored?: boolean;
  /** Adds the accent glow — reserve for hero placements, not list rows. */
  glow?: boolean;
  /** Announce the resource name to assistive tech instead of hiding the glyph. */
  labelled?: boolean;
  className?: string;
}

const COLORS: Record<ResourceKey, string> = {
  titan: 'text-titan',
  silicate: 'text-silicate',
  isotope: 'text-isotope',
  power: 'text-power',
  aether: 'text-aether',
  credit: 'text-credit',
  chronos: 'text-chronos',
};

const GLOWS: Record<ResourceKey, string> = {
  titan: 'drop-shadow-[0_0_6px_rgb(var(--sg-titan)/0.7)]',
  silicate: 'drop-shadow-[0_0_6px_rgb(var(--sg-silicate)/0.7)]',
  isotope: 'drop-shadow-[0_0_6px_rgb(var(--sg-isotope)/0.7)]',
  power: 'drop-shadow-[0_0_6px_rgb(var(--sg-power)/0.7)]',
  aether: 'drop-shadow-[0_0_6px_rgb(var(--sg-aether)/0.7)]',
  credit: 'drop-shadow-[0_0_6px_rgb(var(--sg-credit)/0.7)]',
  chronos: 'drop-shadow-[0_0_6px_rgb(var(--sg-chronos)/0.7)]',
};

/**
 * A resource glyph in its canonical colour — or, when a `ResourceIconProvider`
 * is mounted above it, the rendered-art sprite for that resource.
 *
 * Always prefer this over reaching for the raw icon: it guarantees Titan is
 * amber and Chronos is magenta everywhere in the game (vector fallback), and
 * is the single place that decides whether a spot gets bitmap art at all —
 * every cost list, the top bar and every detail card pick up new art the
 * moment it's added to the sprite catalogue, with no call-site changes.
 */
export function ResourceIcon({
  resource,
  size = 16,
  colored = true,
  glow = false,
  labelled = false,
  className,
}: ResourceIconProps) {
  const resolve = useContext(ResourceIconContext);
  const art = resolve?.(resource);

  if (art) {
    return (
      <img
        src={art}
        alt={labelled ? RESOURCES[resource].label : ''}
        aria-hidden={labelled ? undefined : true}
        width={size}
        height={size}
        loading="lazy"
        className={cn('inline-block shrink-0 object-contain', glow && GLOWS[resource], className)}
        style={{ width: size, height: size }}
      />
    );
  }

  const Glyph = RESOURCE_ICONS[resource];
  return (
    <Glyph
      size={size}
      title={labelled ? RESOURCES[resource].label : undefined}
      className={cn(colored && COLORS[resource], glow && GLOWS[resource], className)}
    />
  );
}
